import {
  BusinessCategoryLike,
  getDisplayedBusinessSubcategories,
  isHealthcareCategoryName,
} from "../../../core/business/category";

export type BookingCategoryBusinessLike = {
  businessCategoryId?: number | null;
  businessSubcategoryId?: number | null;
  businessTopCategory?: BusinessCategoryLike | null;
  businessSubcategory?: BusinessCategoryLike | null;
  careSettings?: {
    afhLicenseNumber?: string | null;
  } | null;
  careMeta?: {
    bedsTotal?: number | null;
    bedsOpen?: number | null;
  } | null;
};

function getCategoryId(category?: BusinessCategoryLike | null) {
  return Number(category?.id || 0);
}

function getParentCategoryId(category?: BusinessCategoryLike | null) {
  return Number(category?.parentCategoryId || category?.parentCategory?.id || 0);
}

function isRealSubcategory(category?: BusinessCategoryLike | null) {
  return getParentCategoryId(category) > 0;
}

function getCategoryFromMap(
  categoryById: Map<number, BusinessCategoryLike>,
  categoryId?: number | string | null
) {
  const normalizedId = Number(categoryId || 0);
  if (!normalizedId) return null;
  return categoryById.get(normalizedId) || null;
}

export function getBookingCategoryDisplayName(
  category: BusinessCategoryLike | null | undefined
) {
  const rawName = String(category?.name || "").trim();
  if (!rawName) return "";
  return isHealthcareCategoryName(rawName) ? "Healthcare" : rawName;
}

export function hasBookingCareSignals(business: BookingCategoryBusinessLike | any) {
  return Boolean(
    business?.careSettings?.afhLicenseNumber ||
      business?.careMeta?.bedsTotal != null ||
      business?.careMeta?.bedsOpen != null
  );
}

export function getResolvedBookingBusinessSubcategory(
  business: BookingCategoryBusinessLike | any,
  categoryById: Map<number, BusinessCategoryLike>
) {
  const directSubcategory = business?.businessSubcategory;
  if (isRealSubcategory(directSubcategory)) {
    return directSubcategory as BusinessCategoryLike;
  }

  const subcategoryFromSubcategoryId = getCategoryFromMap(
    categoryById,
    business?.businessSubcategoryId
  );
  if (isRealSubcategory(subcategoryFromSubcategoryId)) {
    return subcategoryFromSubcategoryId;
  }

  const subcategoryFromCategoryId = getCategoryFromMap(
    categoryById,
    business?.businessCategoryId
  );
  if (isRealSubcategory(subcategoryFromCategoryId)) {
    return subcategoryFromCategoryId;
  }

  return null;
}

export function getResolvedBookingBusinessTopCategory(
  business: BookingCategoryBusinessLike | any,
  categoryById: Map<number, BusinessCategoryLike>,
  healthcareCategory: BusinessCategoryLike | null
) {
  const explicitTopCategory = business?.businessTopCategory;
  if (getCategoryId(explicitTopCategory) > 0) {
    return explicitTopCategory as BusinessCategoryLike;
  }

  const resolvedSubcategory = getResolvedBookingBusinessSubcategory(
    business,
    categoryById
  );
  const resolvedSubcategoryParentId = getParentCategoryId(resolvedSubcategory);
  if (resolvedSubcategoryParentId > 0) {
    return (
      resolvedSubcategory?.parentCategory ||
      getCategoryFromMap(categoryById, resolvedSubcategoryParentId) ||
      null
    );
  }

  const categoryFromBusinessCategoryId = getCategoryFromMap(
    categoryById,
    business?.businessCategoryId
  );
  if (categoryFromBusinessCategoryId && !isRealSubcategory(categoryFromBusinessCategoryId)) {
    return categoryFromBusinessCategoryId;
  }

  const categoryFromBusinessSubcategoryId = getCategoryFromMap(
    categoryById,
    business?.businessSubcategoryId
  );
  if (
    categoryFromBusinessSubcategoryId &&
    !isRealSubcategory(categoryFromBusinessSubcategoryId)
  ) {
    return categoryFromBusinessSubcategoryId;
  }

  if (getCategoryId(healthcareCategory) > 0 && hasBookingCareSignals(business)) {
    return healthcareCategory;
  }

  return null;
}

export function getResolvedBookingBusinessTopCategoryId(
  business: BookingCategoryBusinessLike | any,
  categoryById: Map<number, BusinessCategoryLike>,
  healthcareCategory: BusinessCategoryLike | null
) {
  return getCategoryId(
    getResolvedBookingBusinessTopCategory(business, categoryById, healthcareCategory)
  );
}

export function getResolvedBookingBusinessSubcategoryId(
  business: BookingCategoryBusinessLike | any,
  categoryById: Map<number, BusinessCategoryLike>
) {
  return getCategoryId(getResolvedBookingBusinessSubcategory(business, categoryById));
}

export function getAvailableBookingSubcategories({
  selectedCategoryId,
  categories,
  categoryById,
  healthcareCategory,
  businesses,
}: {
  selectedCategoryId: number | null;
  categories: BusinessCategoryLike[] | null | undefined;
  categoryById: Map<number, BusinessCategoryLike>;
  healthcareCategory: BusinessCategoryLike | null;
  businesses: BookingCategoryBusinessLike[];
}) {
  if (!selectedCategoryId) return [];

  const subcategoryById = new Map<number, BusinessCategoryLike>();
  getDisplayedBusinessSubcategories(categories, selectedCategoryId).forEach(
    (subcategory) => {
      const subcategoryId = getCategoryId(subcategory);
      if (!subcategoryId || subcategoryId === Number(selectedCategoryId)) return;
      subcategoryById.set(subcategoryId, subcategory);
    }
  );

  businesses.forEach((business) => {
    if (
      getResolvedBookingBusinessTopCategoryId(
        business,
        categoryById,
        healthcareCategory
      ) !== Number(selectedCategoryId)
    ) {
      return;
    }

    const resolvedSubcategory = getResolvedBookingBusinessSubcategory(
      business,
      categoryById
    );
    const resolvedSubcategoryId = getCategoryId(resolvedSubcategory);
    if (
      !resolvedSubcategory ||
      !resolvedSubcategoryId ||
      resolvedSubcategoryId === Number(selectedCategoryId) ||
      subcategoryById.has(resolvedSubcategoryId)
    ) {
      return;
    }

    subcategoryById.set(resolvedSubcategoryId, resolvedSubcategory);
  });

  return Array.from(subcategoryById.values()).sort((left, right) =>
    getBookingCategoryDisplayName(left).localeCompare(
      getBookingCategoryDisplayName(right)
    )
  );
}
